/* eslint-disable no-param-reassign, max-params */

import { NECK_LINE_MODES, SURFACES } from "../misc/constants";
import { getDisplayedSize, getSizeWithSteps } from "../utils/sizes";

const DEFAULT_NECK_LABEL = "default";

const initialState = {
  downscaleFactor: 1,
  neckLineMode: NECK_LINE_MODES.DEFAULT,
  customNeckLines: {},
  neckLineLabel: DEFAULT_NECK_LABEL,
  surfaceLabel: SURFACES.FRONT,
  defaultSurface: null,
  preview: null
};

const surfaceModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    neckLineMode(state) {
      return state.neckLineMode;
    },
    neckLineLabel(state) {
      return state.neckLineLabel;
    },
    customNeckLines(state) {
      return state.customNeckLines;
    },
    surface(state, _, __, rootGetters) {
      const { surfaces } = rootGetters["product/currentComposition"];
      return surfaces ? surfaces[state.surfaceLabel] : state.defaultSurface;
    },
    downscaleFactor(state) {
      return state.downscaleFactor;
    },
    surfaceLabel(state) {
      return state.surfaceLabel;
    },
    convertPxToInches: (_, getters) => px =>
      getDisplayedSize(px / getters.surface.ppi / getters.downscaleFactor),
    convertInchesToPx: (_, getters) => inches =>
      inches * getters.surface.ppi * getters.downscaleFactor,
    oneInch(state, getters) {
      return getters.surface.ppi * getters.downscaleFactor;
    },
    getSurface: (state, _, __, rootGetters) => label => {
      const { surfaces } = rootGetters["product/currentComposition"];
      return surfaces ? surfaces[label] : state.defaultSurface;
    },
    getPreview(state) {
      return state.preview;
    },
    serializedNeckLines:
      (_, { surfaceLabel, customNeckLines, convertPxToInches, neckLineMode }, __, rootGetters) =>
      artworkHeight => {
        if (neckLineMode === NECK_LINE_MODES.DEFAULT) return {};

        const filledSizes = rootGetters["app/filledSizes"];
        const defaultNeckLines = rootGetters["product/neckLines"][surfaceLabel];
        const allLines = { ...defaultNeckLines, ...customNeckLines };

        return Object.keys(allLines).reduce((acc, key) => {
          if (filledSizes.includes(key)) {
            const maxDistance = getSizeWithSteps(
              defaultNeckLines[key].max_height - convertPxToInches(artworkHeight)
            );
            acc[key] = Math.min(allLines[key].default || allLines[key], maxDistance);
          }
          return acc;
        }, {});
      },
    maxInchSizes: (_, getters) => {
      const { surface } = getters;
      return {
        maxWidth: (surface.bounding_box.width / surface.ppi).toFixed(2),
        maxHeight: (surface.bounding_box.height / surface.ppi).toFixed(2)
      };
    }
  },

  mutations: {
    setDownscaleFactor(state, downscaleFactor) {
      state.downscaleFactor = downscaleFactor;
    },
    setDefaultSurface(state, defaultSurface) {
      state.defaultSurface = defaultSurface;
    },
    setSurfaceLabel(state, surfaceLabel) {
      state.surfaceLabel = surfaceLabel;
    },
    setNeckLineMode(state, neckLineMode) {
      state.neckLineMode = neckLineMode;
    },
    setNeckLineLabel(state, neckLineLabel) {
      state.neckLineLabel = neckLineLabel || DEFAULT_NECK_LABEL;
    },
    setDataFromPreset(state, preset) {
      state.surfaceLabel = preset.position;
      state.customNeckLines = preset.custom_neck_distances || {};
    },
    setPreview(state, preview) {
      state.preview = preview;
    }
  },

  actions: {
    setSurfaceLabel({ state, dispatch }, surfaceLabel) {
      state.surfaceLabel = surfaceLabel;
      dispatch("sendQueryToPreset", {
        position: surfaceLabel
      });
    },
    setNeckLineHeight({ state, dispatch }, { neckLineLabel, height }) {
      state.customNeckLines = {
        ...state.customNeckLines,
        [neckLineLabel]: height
      };
      dispatch("sendQueryToPreset", {
        custom_neck_distances: state.customNeckLines
      });
    },
    clearNeckLines({ commit, state, dispatch }) {
      commit("setNeckLineMode", NECK_LINE_MODES.DEFAULT);
      commit("setNeckLineLabel", DEFAULT_NECK_LABEL);
      state.customNeckLines = {};
      dispatch("sendQueryToPreset", {
        custom_neck_distances: null
      });
    },
    sendQueryToPreset({ state, dispatch }, query) {
      const side = state.surfaceLabel === SURFACES.BACK ? "back" : "front";
      dispatch(
        `preset_${side}/addToPresetQueue`,
        {
          ...query
        },
        { root: true }
      );
    }
  }
};

export default surfaceModule;
